import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Avatar,
  IconButton,
  FormControlLabel,
  Switch,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
} from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useForm, Controller } from 'react-hook-form';
import { companiesApi, Company, EmailConfig } from '../services/companies';
import { useAuth } from '../contexts/AuthContext';
import { CURRENCIES } from '../utils/currency';

const CompanyConfiguration: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [company, setCompany] = useState<Company | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [newCategory, setNewCategory] = useState('');
  const [showEmailConfig, setShowEmailConfig] = useState(false);
  const { register, handleSubmit, control, formState: { errors }, reset, watch, setValue } = useForm<{
    name: string;
    location?: string;
    companyType?: string;
    numberOfEmployees: number;
    baseCurrency?: string;
    openingBalance?: number;
    enableNonBillableTime?: boolean;
    nonBillableCategories?: string[];
    emailConfig?: EmailConfig;
    // Bank Account Details (UK)
    bankAccountName?: string;
    bankAccountNumber?: string;
    bankSortCode?: string;
    // International Bank Details
    bankCurrencyAccepted?: string;
    bankIban?: string;
    bankBic?: string;
    // Contact Details
    contactPhone?: string;
    contactEmail?: string;
  }>();

  const numberOfEmployees = watch('numberOfEmployees');
  const enableNonBillableTime = watch('enableNonBillableTime');
  const nonBillableCategories = watch('nonBillableCategories') || [];

  useEffect(() => {
    loadCompany();
  }, []);

  const loadCompany = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get user's company ID from auth context or API
      // For now, we'll need to add this to the auth context
      // Assuming we can get it from the user object
      if (user?.companyId) {
        const companyData = await companiesApi.getById(user.companyId);
        setCompany(companyData);
        reset({
          name: companyData.name,
          location: companyData.location || '',
          companyType: companyData.companyType || '',
          numberOfEmployees: companyData.numberOfEmployees,
          baseCurrency: companyData.baseCurrency || 'USD',
          openingBalance: companyData.openingBalance || 0,
          enableNonBillableTime: companyData.enableNonBillableTime || false,
          nonBillableCategories: companyData.nonBillableCategories || [],
          emailConfig: companyData.emailConfig || undefined,
          // Bank Account Details (UK)
          bankAccountName: companyData.bankAccountName || '',
          bankAccountNumber: companyData.bankAccountNumber || '',
          bankSortCode: companyData.bankSortCode || '',
          // International Bank Details
          bankCurrencyAccepted: companyData.bankCurrencyAccepted || '',
          bankIban: companyData.bankIban || '',
          bankBic: companyData.bankBic || '',
          // Contact Details
          contactPhone: companyData.contactPhone || '',
          contactEmail: companyData.contactEmail || '',
        });
        setShowEmailConfig(!!companyData.emailConfig);
        // Set logo preview if logo exists
        if (companyData.logoUrl) {
          const logoUrl = companyData.logoUrl.startsWith('http') 
            ? companyData.logoUrl 
            : `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}${companyData.logoUrl}`;
          setLogoPreview(logoUrl);
        } else {
          setLogoPreview(null);
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load company information');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!company || !event.target.files || event.target.files.length === 0) return;

    const file = event.target.files[0];
    
    // Validate file type
    if (!file.type.match('image.*')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return;
    }

    try {
      setUploadingLogo(true);
      setError(null);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);

      // Upload logo
      console.log('Uploading logo for company:', company.id);
      const updatedCompany = await companiesApi.uploadLogo(company.id, file);
      console.log('Logo upload response:', updatedCompany);
      setCompany(updatedCompany);
      
      // Update logo preview with the new URL from server
      if (updatedCompany.logoUrl) {
        const logoUrl = updatedCompany.logoUrl.startsWith('http') 
          ? updatedCompany.logoUrl 
          : `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}${updatedCompany.logoUrl}`;
        console.log('Setting logo preview URL:', logoUrl);
        setLogoPreview(logoUrl);
      }
      
      // Force Layout to refresh
      window.dispatchEvent(new Event('companyUpdated'));
      
      setSuccess('Logo uploaded successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Logo upload error:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to upload logo';
      setError(errorMessage);
      setLogoPreview(null);
    } finally {
      setUploadingLogo(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleAddCategory = () => {
    if (newCategory.trim() && !nonBillableCategories.includes(newCategory.trim())) {
      const updated = [...nonBillableCategories, newCategory.trim()];
      setValue('nonBillableCategories', updated);
      setNewCategory('');
    }
  };

  const handleRemoveCategory = (index: number) => {
    const updated = nonBillableCategories.filter((_, i) => i !== index);
    setValue('nonBillableCategories', updated);
  };

  const onSubmit = async (data: any) => {
    if (!company) return;

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      // Prepare update data - convert empty strings to null/undefined for optional fields
      const updateData: any = {
        name: data.name,
        numberOfEmployees: data.numberOfEmployees,
      };
      
      if (data.location && data.location.trim()) {
        updateData.location = data.location.trim();
      } else {
        updateData.location = null;
      }
      
      if (data.companyType && data.companyType.trim()) {
        updateData.companyType = data.companyType.trim();
      } else {
        updateData.companyType = null;
      }
      
      if (data.baseCurrency) {
        updateData.baseCurrency = data.baseCurrency;
      } else {
        updateData.baseCurrency = 'USD';
      }
      
      if (data.openingBalance !== undefined) {
        updateData.openingBalance = parseFloat(data.openingBalance.toString()) || 0;
      }
      
      if (data.enableNonBillableTime !== undefined) {
        updateData.enableNonBillableTime = data.enableNonBillableTime;
      }
      
      if (data.nonBillableCategories !== undefined) {
        updateData.nonBillableCategories = data.nonBillableCategories;
      }
      
      if (showEmailConfig && data.emailConfig) {
        // Only include emailConfig if it has all required fields
        if (data.emailConfig.host && data.emailConfig.port && data.emailConfig.auth?.user && data.emailConfig.auth?.pass && data.emailConfig.fromEmail) {
          updateData.emailConfig = {
            host: data.emailConfig.host.trim(),
            port: typeof data.emailConfig.port === 'string' ? parseInt(data.emailConfig.port) : data.emailConfig.port,
            secure: data.emailConfig.secure || false,
            auth: {
              user: data.emailConfig.auth.user.trim(),
              pass: data.emailConfig.auth.pass,
            },
            fromEmail: data.emailConfig.fromEmail.trim(),
            fromName: data.emailConfig.fromName?.trim() || undefined,
          };
        } else {
          // If showing email config but fields are incomplete, don't update it
          // (keep existing config or leave empty)
        }
      } else if (!showEmailConfig) {
        // If email config is disabled, set it to null to clear it
        updateData.emailConfig = null as any;
      }

      // Bank Account Details (UK) - Always include these fields
      updateData.bankAccountName = (data.bankAccountName || '').trim() || null;
      updateData.bankAccountNumber = (data.bankAccountNumber || '').trim() || null;
      updateData.bankSortCode = (data.bankSortCode || '').trim() || null;
      
      // International Bank Details - Always include these fields
      updateData.bankCurrencyAccepted = (data.bankCurrencyAccepted || '').trim() || null;
      updateData.bankIban = (data.bankIban || '').trim() || null;
      updateData.bankBic = (data.bankBic || '').trim() || null;
      
      // Contact Details - Always include these fields
      updateData.contactPhone = (data.contactPhone || '').trim() || null;
      updateData.contactEmail = (data.contactEmail || '').trim() || null;

      console.log('Updating company with data:', updateData);
      const updatedCompany = await companiesApi.update(company.id, updateData);
      console.log('Company updated successfully:', updatedCompany);
      setCompany(updatedCompany);
      setSuccess('Company configuration updated successfully');
      
      // Trigger a page reload to refresh Layout component
      // Reload company to get fresh data
      await loadCompany();
      
      // Force Layout to refresh by triggering a window event
      window.dispatchEvent(new Event('companyUpdated'));
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Update company error:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to update company configuration';
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Company Configuration
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Box display="flex" alignItems="flex-start" gap={3} mb={2}>
                <Avatar
                  src={logoPreview || (company?.logoUrl ? (company.logoUrl.startsWith('http') 
                    ? company.logoUrl 
                    : `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}${company.logoUrl}`) : undefined)}
                  alt={company?.name || 'Company Logo'}
                  sx={{ width: 150, height: 150 }}
                  variant="rounded"
                >
                  {company?.name?.charAt(0).toUpperCase() || 'C'}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" gutterBottom>Company Logo</Typography>
                  <input
                    ref={fileInputRef}
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="logo-upload"
                    type="file"
                    onChange={handleLogoUpload}
                    disabled={uploadingLogo}
                  />
                  <label htmlFor="logo-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<PhotoCameraIcon />}
                      disabled={uploadingLogo}
                      sx={{ mt: 1 }}
                    >
                      {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                    </Button>
                  </label>
                  <Typography variant="caption" display="block" sx={{ mt: 1.5 }}>
                    Recommended: Square image, max 5MB (JPEG, PNG, GIF, WebP, SVG)
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Company Name"
                required
                {...register('name', { required: 'Company name is required' })}
                error={!!errors.name}
                helperText={errors.name?.message}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Location"
                {...register('location')}
                error={!!errors.location}
                helperText={errors.location?.message}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="companyType"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Company Type</InputLabel>
                    <Select {...field} label="Company Type">
                      <MenuItem value="Sole Trader">Sole Trader</MenuItem>
                      <MenuItem value="Partnership">Partnership</MenuItem>
                      <MenuItem value="Limited Company">Limited Company</MenuItem>
                      <MenuItem value="LLC">LLC</MenuItem>
                      <MenuItem value="Corporation">Corporation</MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="numberOfEmployees"
                control={control}
                rules={{ required: 'Number of employees is required', min: { value: 1, message: 'Must be at least 1' } }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="number"
                    label="Number of Employees"
                    required
                    inputProps={{ min: 1 }}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message || (numberOfEmployees === 1 ? 'Sole trader setup - Full admin access' : 'Multi-user setup - User management enabled')}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="baseCurrency"
                control={control}
                rules={{ required: 'Base currency is required' }}
                render={({ field, fieldState }) => (
                  <FormControl fullWidth required error={!!fieldState.error}>
                    <InputLabel>Base Currency</InputLabel>
                    <Select {...field} label="Base Currency">
                      {CURRENCIES.map((currency) => (
                        <MenuItem key={currency.code} value={currency.code}>
                          {currency.code} - {currency.name} ({currency.symbol})
                        </MenuItem>
                      ))}
                    </Select>
                    {fieldState.error && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                        {fieldState.error.message}
                      </Typography>
                    )}
                    <Typography variant="caption" display="block" sx={{ mt: 0.5, ml: 1.75 }}>
                      This currency will be used for all financial displays throughout the application
                    </Typography>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Opening Balance (Optional)"
                inputProps={{ step: 0.01, min: 0 }}
                {...register('openingBalance', {
                  valueAsNumber: true,
                  min: { value: 0, message: 'Opening balance must be positive' },
                })}
                error={!!errors.openingBalance}
                helperText={errors.openingBalance?.message || 'Starting balance for accounting purposes (defaults to 0 if not provided)'}
              />
            </Grid>
            
            {/* Bank Account Details Section */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }}>
                <Typography variant="h6" color="text.secondary">
                  Bank Account Details
                </Typography>
              </Divider>
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                UK Bank Account
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Account Name"
                {...register('bankAccountName')}
                placeholder="e.g., Business Account"
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Account Number"
                {...register('bankAccountNumber')}
                placeholder="e.g., 12345678"
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Sort Code"
                {...register('bankSortCode')}
                placeholder="e.g., 12-34-56"
                helperText="Format: XX-XX-XX"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                International Payments
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller
                name="bankCurrencyAccepted"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Currency Accepted</InputLabel>
                    <Select {...field} label="Currency Accepted">
                      <MenuItem value="">None</MenuItem>
                      {CURRENCIES.map((currency) => (
                        <MenuItem key={currency.code} value={currency.code}>
                          {currency.code} - {currency.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="IBAN"
                {...register('bankIban')}
                placeholder="e.g., GB82 WEST 1234 5698 7654 32"
                helperText="International Bank Account Number"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="BIC/SWIFT"
                {...register('bankBic')}
                placeholder="e.g., WESTGB22"
                helperText="Bank Identifier Code"
              />
            </Grid>
            
            {/* Contact Details Section */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }}>
                <Typography variant="h6" color="text.secondary">
                  Contact Details
                </Typography>
              </Divider>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone Number"
                {...register('contactPhone')}
                placeholder="e.g., +44 20 1234 5678"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="email"
                label="Contact Email Address"
                {...register('contactEmail', {
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: 'Please enter a valid email address',
                  },
                })}
                error={!!errors.contactEmail}
                helperText={errors.contactEmail?.message || 'Email address for invoice communications'}
                placeholder="e.g., info@company.com"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Controller
                name="enableNonBillableTime"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={field.value || false}
                        onChange={field.onChange}
                        color="primary"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body1">Enable Non-Billable Time Tracking</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Allow users to mark timesheet entries as non-billable. Non-billable entries will be excluded from invoice generation.
                        </Typography>
                      </Box>
                    }
                  />
                )}
              />
            </Grid>
            {enableNonBillableTime && (
              <Grid item xs={12}>
                <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                  <Typography variant="h6" gutterBottom>
                    Non-Billable Time Categories
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                    Add categories to classify non-billable time entries (e.g., Training, Administrative, Sick Leave)
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <TextField
                      size="small"
                      placeholder="Add category"
                      value={newCategory}
                      onChange={(e) => setNewCategory(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddCategory();
                        }
                      }}
                      sx={{ flexGrow: 1 }}
                    />
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={handleAddCategory}
                      disabled={!newCategory.trim()}
                    >
                      Add
                    </Button>
                  </Box>
                  {nonBillableCategories.length > 0 ? (
                    <List dense>
                      {nonBillableCategories.map((category, index) => (
                        <ListItem key={index} sx={{ px: 0 }}>
                          <ListItemText primary={category} />
                          <ListItemSecondaryAction>
                            <IconButton
                              edge="end"
                              size="small"
                              onClick={() => handleRemoveCategory(index)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      No categories added yet
                    </Typography>
                  )}
                </Paper>
              </Grid>
            )}
            <Grid item xs={12}>
              <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Box>
                    <Typography variant="h6">Email (SMTP) Configuration</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Configure SMTP settings to enable sending invoices via email
                    </Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={showEmailConfig}
                        onChange={(e) => {
                          setShowEmailConfig(e.target.checked);
                          if (!e.target.checked) {
                            setValue('emailConfig', undefined);
                          } else {
                            setValue('emailConfig', {
                              host: '',
                              port: 587,
                              secure: false,
                              auth: { user: '', pass: '' },
                              fromEmail: '',
                              fromName: company?.name || '',
                            });
                          }
                        }}
                        color="primary"
                      />
                    }
                    label={showEmailConfig ? 'Enabled' : 'Disabled'}
                  />
                </Box>
                {showEmailConfig && (
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="emailConfig.host"
                        control={control}
                        rules={{ required: showEmailConfig ? 'SMTP host is required' : false }}
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="SMTP Host"
                            required={showEmailConfig}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message || 'e.g., smtp.gmail.com, smtp.outlook.com'}
                            placeholder="smtp.gmail.com"
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="emailConfig.port"
                        control={control}
                        rules={{
                          required: showEmailConfig ? 'SMTP port is required' : false,
                          min: { value: 1, message: 'Port must be between 1 and 65535' },
                          max: { value: 65535, message: 'Port must be between 1 and 65535' },
                        }}
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            fullWidth
                            type="number"
                            label="SMTP Port"
                            required={showEmailConfig}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message || 'Common: 587 (TLS), 465 (SSL), 25'}
                            inputProps={{ min: 1, max: 65535 }}
                            onChange={(e) => field.onChange(parseInt(e.target.value) || '')}
                            value={field.value || ''}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Controller
                        name="emailConfig.secure"
                        control={control}
                        render={({ field }) => (
                          <FormControlLabel
                            control={
                              <Switch
                                checked={field.value || false}
                                onChange={field.onChange}
                                color="primary"
                              />
                            }
                            label={
                              <Box>
                                <Typography variant="body2">Use SSL/TLS (Secure Connection)</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Enable for port 465 (SSL), disable for port 587 (TLS/STARTTLS)
                                </Typography>
                              </Box>
                            }
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="emailConfig.auth.user"
                        control={control}
                        rules={{ required: showEmailConfig ? 'SMTP username is required' : false }}
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="SMTP Username"
                            required={showEmailConfig}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message || 'Your email address or SMTP username'}
                            placeholder="your-email@example.com"
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="emailConfig.auth.pass"
                        control={control}
                        rules={{ required: showEmailConfig ? 'SMTP password is required' : false }}
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            fullWidth
                            type="password"
                            label="SMTP Password"
                            required={showEmailConfig}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message || 'Your email password or app-specific password'}
                            placeholder="••••••••"
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="emailConfig.fromEmail"
                        control={control}
                        rules={{
                          required: showEmailConfig ? 'From email is required' : false,
                          pattern: showEmailConfig
                            ? {
                                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                message: 'Invalid email address',
                              }
                            : undefined,
                        }}
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            fullWidth
                            type="email"
                            label="From Email Address"
                            required={showEmailConfig}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message || 'Email address that will appear as sender'}
                            placeholder="noreply@example.com"
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="emailConfig.fromName"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="From Name (Optional)"
                            helperText="Display name for the sender (defaults to company name)"
                            placeholder={company?.name || 'Company Name'}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Alert severity="info">
                        <Typography variant="body2" gutterBottom>
                          <strong>Common SMTP Settings:</strong>
                        </Typography>
                        <Typography variant="caption" component="div">
                          <strong>Gmail:</strong> smtp.gmail.com, Port 587, TLS enabled, Use app password
                          <br />
                          <strong>Outlook/Hotmail:</strong> smtp-mail.outlook.com, Port 587, TLS enabled
                          <br />
                          <strong>Yahoo:</strong> smtp.mail.yahoo.com, Port 587, TLS enabled
                        </Typography>
                      </Alert>
                    </Grid>
                  </Grid>
                )}
              </Paper>
            </Grid>
            <Grid item xs={12}>
              {numberOfEmployees === 1 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Sole trader mode: You have full admin access. Timesheets will be auto-approved when submitted.
                </Alert>
              )}
              {numberOfEmployees > 1 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Multi-user mode: You can manage users and their roles. Go to User Management to add employees.
                </Alert>
              )}
            </Grid>
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                disabled={saving}
                sx={{ mr: 2 }}
              >
                {saving ? 'Saving...' : 'Save Configuration'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

export default CompanyConfiguration;






